import debounce from "lodash.debounce";
import { useCallback, useEffect, useRef, useState } from "react";
import type { LegacyTabIndicatorStyle } from "./TabList.types";

const useTabListIndicatorStyle = (activeTab: HTMLElement | null) => {
  const [tabIndicatorStyle, setTabIndicatorStyle] = useState<LegacyTabIndicatorStyle>();

  const getActiveTabOffset = useCallback(() => {
    if (!activeTab) return undefined;

    return {
      width: activeTab.offsetWidth,
      left: activeTab.offsetLeft,
      height: activeTab.offsetHeight,
    };
  }, [activeTab]);

  useEffect(() => {
    setTabIndicatorStyle(getActiveTabOffset);
  }, [getActiveTabOffset]);

  useEffect(() => {
    const debouncedSetTabIndicatorStyle = debounce(() => {
      setTabIndicatorStyle(getActiveTabOffset);
    }, 100);

    window.addEventListener("resize", debouncedSetTabIndicatorStyle);

    return () => {
      window.removeEventListener("resize", debouncedSetTabIndicatorStyle);
      debouncedSetTabIndicatorStyle.cancel();
    };
  }, [getActiveTabOffset]);

  return tabIndicatorStyle;
};

export const useTabs = <T extends string>(props: { defaultValue: T }) => {
  const [value, setValue] = useState<T>(props?.defaultValue);
  const tabRefObject = useRef<{ value: T; ref: HTMLElement }[]>([]);
  const [activeTabRef, setActiveTabRef] = useState<HTMLElement | null>(null);
  const tabIndicatorStyle = useTabListIndicatorStyle(activeTabRef);

  useEffect(() => {
    const activeIndex = tabRefObject.current.findIndex((tab) => tab.value === value);
    setActiveTabRef(tabRefObject.current[activeIndex]?.ref ?? null);
  }, [value]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const activeIndex = tabRefObject.current.findIndex((tab) => tab.value === value);
      if (activeIndex === -1) return;

      const activeTab = tabRefObject.current[activeIndex];
      const isClayTabActive =
        document.activeElement?.role === "tab" &&
        document.activeElement.id === `clay-tab-${activeTab.value}`;

      if (!isClayTabActive) return;

      const tabKeyList = tabRefObject.current.map((tab) => tab.value);

      const focusNextTab = (nextKey: string) => {
        document.getElementById(`clay-tab-${nextKey}`)?.focus();
      };

      const move = (step: number) => {
        const len = tabKeyList.length;
        const nextKey = tabKeyList[(((activeIndex + step) % len) + len) % len];
        setValue(nextKey);
        focusNextTab(nextKey);
      };

      switch (event.key) {
        case "ArrowLeft":
          move(-1);
          break;
        case "ArrowRight":
          move(1);
          break;
        case "Home":
          focusNextTab(tabKeyList[0]);
          break;
        case "End":
          focusNextTab(tabKeyList[tabKeyList.length - 1]);
          break;

        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [value]);

  const getTabProps = (tabValue: T, index: number) => ({
    key: tabValue,
    id: tabValue,
    active: value === tabValue,
    ref: (ref: HTMLButtonElement) => {
      tabRefObject.current[index] = { value: tabValue, ref };
    },
    onClick: () => {
      setValue(tabValue);
    },
    value,
  });

  return {
    value,
    setValue,
    getTabProps,
    tabIndicatorStyle,
  };
};
